import React, { FC, useEffect } from 'react';
import { dispatch, Store } from '@/redux';
import { useSelector } from 'react-redux';
import { GetCoinsResponse } from '../types';
import { Box, Button, Typography } from '@mui/material';
import { BhabiLink, ErrorLayout, LoadingLayout } from '@/shared/components';
import { InRoomPlayerList, RenderGamePlay } from './components/playground';
import { WinCelebration } from './components/win-celebration';
import { TurnBanner } from './components/turn-banner';
import { useHttpQuery } from '@/api';
import { API_URLS } from '../consts';
import { ThrowCardProvider } from './context';
import { useLeaveRoom } from '../components';
import { INVITE_PLAYER_ROUTE } from '../invite-players';
import { updateUserCoins } from '@/redux/reducers';
import { SharePanel } from '@/screens/guest/components/share-panel';
import { useInPlayEngine } from './use-in-play-engine';

export const InPlayPageWithoutCtx: FC = () => {
  const { coins: currentCoins } = useSelector(
    (state: Store) => state.userReducer
  );
  const {
    userID,
    inPlayResponse,
    gameInfo,
    socketError,
    connectionState,
    reconnect,
    sendMessage,
    selectedCard,
    setSelectedCard,
    onClickCard,
    userDeck,
    lockDrag,
    isLandscapeMode,
    showWinCelebration,
    setShowWinCelebration,
  } = useInPlayEngine();

  const { data } = useHttpQuery<GetCoinsResponse>(
    ['currentUserCoins', userID].join('-'),
    API_URLS.GET_COINS,
    'GET'
  );

  useEffect(() => {
    if (!currentCoins || !inPlayResponse) return;
    if (data?.balance !== currentCoins) {
      dispatch(updateUserCoins(Number(data?.balance) || 0));
    }
  }, [currentCoins, data?.balance, inPlayResponse]);

  const leaveRoom = useLeaveRoom(sendMessage);

  if (!inPlayResponse && !socketError) {
    return <LoadingLayout height="auto" />;
  }

  if (!inPlayResponse && socketError) {
    return (
      <ErrorLayout
        level="error"
        texts={{ defaultError: 'Something went wrong, try rejoining room.' }}
        outerBoxProps={{
          height: 'auto',
        }}
      >
        <Button
          variant="contained"
          onClick={leaveRoom(
            `Are you sure you want to leave this room?`,
            false,
            [INVITE_PLAYER_ROUTE.ABSOLUTE_PATH, 'tab=Rooms'].join('?')
          )}
        >
          Leave Room
        </Button>
      </ErrorLayout>
    );
  }

  return (
    <div className="relative h-[calc(100vh-50px)] overflow-y-hidden overflow-x-hidden pt-3">
      {/* celebration pop up when user wins */}
      {showWinCelebration && (
        <WinCelebration onComplete={() => setShowWinCelebration(false)} />
      )}

      {!gameInfo?.room?.isFull && gameInfo?.room?.roomID && (
        <div className="st-card" style={{ margin: '0 12px 12px' }}>
          <SharePanel
            roomID={gameInfo.room.roomID}
            seats={gameInfo.room.numberOfPlayers}
          />
        </div>
      )}
      {/* waiting for players to join message */}
      {!gameInfo?.room?.isFull && inPlayResponse?.players?.length && (
        <div className="flex flex-col justify-center px-2">
          <ErrorLayout
            level="info"
            texts={{ defaultError: 'Waiting for all the players to join' }}
            outerBoxProps={{
              height: 'auto',
              sx: {
                justifyContent: 'flex-start',
                gap: 0.5,
              },
            }}
          />
          <Typography className="text-text-primary text-center">
            Don`t have enough players?{' '}
            {
              <BhabiLink
                to={[INVITE_PLAYER_ROUTE.ABSOLUTE_PATH, 'tab=Bots'].join('?')}
              >
                Invite bots
              </BhabiLink>
            }{' '}
            to start the game.
          </Typography>
          {inPlayResponse && (
            <Box className="flex align-center justify-center mt-2 w-full">
              <InRoomPlayerList
                nextTurn={inPlayResponse.gameInfo.room?.nextTurn ?? ''}
                noOfPlayers={inPlayResponse.gameInfo.room?.numberOfPlayers ?? 0}
                players={inPlayResponse.players}
                isHorizontal
              />
            </Box>
          )}
        </div>
      )}

      {gameInfo?.room?.isFull && (
        <div className="px-2">
          <TurnBanner
            compact
            connectionState={connectionState}
            onReconnect={reconnect}
          />
        </div>
      )}

      <RenderGamePlay
        inPlayResponse={inPlayResponse}
        sendMessage={sendMessage}
        showPlayGround={Boolean(
          gameInfo?.room?.isFull && inPlayResponse?.players?.length
        )}
        selectedCard={selectedCard}
        gameInfo={gameInfo}
        setSelectedCard={setSelectedCard}
        userDeck={userDeck}
        userID={userID}
        lockDrag={lockDrag}
        onClickCard={onClickCard}
        isLandscapeMode={isLandscapeMode}
      />
    </div>
  );
};

export const InPlayPage: FC = () => (
  <ThrowCardProvider>
    <InPlayPageWithoutCtx />
  </ThrowCardProvider>
);
